import { BuildUri } from './BuildUri'
import { NetworkManager } from '../context'
import context from '../context'

export interface ElementOverride {
  property: string
  label: string
}

export type BadgeElementType =
  | 'PHOTO'
  | 'TEXT'
  | 'BARCODE'
  | 'LOGO'
  | 'RECT'
  | 'LINE'
  | 'IMAGE'
  | 'ELLIPSE'
  | 'PATH'
  | 'CUSTOM_PATH'

export type PathShape =
  | 'STAR'
  | 'HEXAGON'
  | 'SHIELD'
  | 'DIAMOND'
  | 'HEART'
  | 'ARROW'
  | 'RIBBON'

export interface PathPoint {
  x: number
  y: number
  curve?: boolean
  cx1?: number
  cy1?: number
  cx2?: number
  cy2?: number
}

export interface BadgeTemplateElement {
  id: string
  type: BadgeElementType
  x: number
  y: number
  width: number
  height: number
  // Text properties
  field?: string
  fontSize?: number
  fontWeight?: 'NORMAL' | 'BOLD'
  align?: 'LEFT' | 'CENTER' | 'RIGHT'
  uppercase?: boolean
  backgroundColor?: string
  // Common visual properties
  color?: string
  opacity?: number
  borderRadius?: number
  borderColor?: string
  borderWidth?: number
  // Line properties
  lineWidth?: number
  // Image properties
  imageId?: number
  imageData?: string // legacy/preview only
  // Path/shape properties
  shape?: PathShape
  // Custom path properties
  points?: PathPoint[]
  closed?: boolean
  // Override config
  overrides?: ElementOverride[]
}

export interface BadgeTemplateLayoutDefinition {
  elements: BadgeTemplateElement[]
}

export interface BadgeTemplate {
  id: number
  name: string
  description?: string
  category: string
  isSystem: boolean
  isActive: boolean
  orientation: string
  size: string
  width: number
  height: number
  backgroundColor?: string
  layoutDefinition?: BadgeTemplateLayoutDefinition | null
  defaultFieldMappings?: Record<string, string> | null
  isLayoutBased: boolean
  isGlobal: boolean
  clientIds: number[]
  svgTemplatePath?: string
  createdBy: { id: number; name: string } | null
  createdAt: string
  updatedAt: string
}

export interface BadgeTemplateRequest {
  name: string
  description?: string
  category?: string
  isActive?: boolean
  orientation?: string
  size?: string
  width?: number
  height?: number
  backgroundColor?: string
  layoutDefinition?: BadgeTemplateLayoutDefinition | null
  defaultFieldMappings?: Record<string, string> | null
  isGlobal?: boolean
  clientIds?: number[]
}

export interface EventBadgeConfig {
  id: number
  eventId: number
  templateId: number
  template: BadgeTemplate
  propertyOverrides?: Record<string, string>
  createdAt: string
  updatedAt: string
}

export interface AvailableField {
  key: string
  label: string
}

/** Fills a preview's role/event fields from a role form, saved or not */
export interface BadgePreviewRoleContext {
  eventId: number
  roleBadgeName?: string | null
  accreditationLevelId?: number | null
  badgeName?: string | null
}

class BadgeTemplateEndpointsClass {
  getTemplates(
    active?: boolean,
    category?: string,
    clientId?: number
  ): Promise<BadgeTemplate[]> {
    return NetworkManager.fetch(
      BuildUri('/badge-templates', {}, { active, category, clientId })
    )
  }

  getTemplate(id: number): Promise<BadgeTemplate> {
    return NetworkManager.fetch(BuildUri('/badge-templates/{id}', { id }))
  }

  createTemplate(request: BadgeTemplateRequest): Promise<BadgeTemplate> {
    return NetworkManager.fetch(BuildUri('/badge-templates'), {
      method: 'POST',
      body: request,
    })
  }

  updateTemplate(
    id: number,
    request: BadgeTemplateRequest
  ): Promise<BadgeTemplate> {
    return NetworkManager.fetch(BuildUri('/badge-templates/{id}', { id }), {
      method: 'PUT',
      body: request,
    })
  }

  deleteTemplate(id: number): Promise<void> {
    return NetworkManager.fetch(BuildUri('/badge-templates/{id}', { id }), {
      method: 'DELETE',
    })
  }

  toggleActive(id: number, isActive: boolean): Promise<BadgeTemplate> {
    return NetworkManager.fetch(
      BuildUri('/badge-templates/{id}/status', { id }),
      { method: 'PATCH', body: { isActive } }
    )
  }

  duplicateTemplate(id: number, name: string): Promise<BadgeTemplate> {
    return NetworkManager.fetch(
      BuildUri('/badge-templates/{id}/duplicate', { id }),
      { method: 'POST', body: { name } }
    )
  }

  getUsage(
    id: number
  ): Promise<{
    eventCount: number
    events: Array<{ id: number; name: string }>
  }> {
    return NetworkManager.fetch(BuildUri('/badge-templates/{id}/usage', { id }))
  }

  uploadImage(
    base64: string,
    contentType: string
  ): Promise<{ id: number; contentType: string }> {
    return NetworkManager.fetch(BuildUri('/badge-templates/upload-image'), {
      method: 'POST',
      body: { base64, contentType },
    })
  }

  getImage(
    imageId: number
  ): Promise<{ id: number; contentType: string; data: string }> {
    return NetworkManager.fetch(
      BuildUri('/badge-templates/image/{imageId}', { imageId })
    )
  }

  getAvailableFields(): Promise<AvailableField[]> {
    return NetworkManager.fetch(BuildUri('/badge-templates/available-fields'))
  }

  previewTemplate(
    id: number,
    propertyOverrides?: Record<string, string>,
    logoBase64?: string | null,
    logoContentType?: string | null,
    roleContext?: BadgePreviewRoleContext | null,
    defaultPictureBase64?: string | null,
    defaultPictureContentType?: string | null
  ): Promise<Response> {
    return context.fetch(BuildUri('/badge-templates/{id}/preview', { id }), {
      method: 'POST',
      body: {
        propertyOverrides: propertyOverrides || {},
        logoBase64: logoBase64 || null,
        logoContentType: logoContentType || null,
        roleContext: roleContext || null,
        defaultPictureBase64: defaultPictureBase64 || null,
        defaultPictureContentType: defaultPictureContentType || null,
      },
    })
  }

  getEventBadgeConfig(eventId: number): Promise<EventBadgeConfig | null> {
    return NetworkManager.fetch(
      BuildUri('/badge-templates/event/{eventId}/config', { eventId })
    )
  }

  updateEventBadgeConfig(
    eventId: number,
    request: any
  ): Promise<EventBadgeConfig> {
    return NetworkManager.fetch(
      BuildUri('/badge-templates/event/{eventId}/config', { eventId }),
      { method: 'PUT', body: request }
    )
  }
}

export default new BadgeTemplateEndpointsClass()
